import { useCallback, useMemo } from "react"

import { exists } from "shared/lib/exists"

import Fuzzy from "@leeoniya/ufuzzy"

interface UseFuzzySearchWithRangesArgs<T> {
  getSearchableString: (item: T) => string
  options?: Fuzzy.Options
}

export interface FuzzySearchResult<T> {
  item: T
  ranges: number[]
}

export function useFuzzySearchWithRanges<T>({
  options,
  getSearchableString
}: UseFuzzySearchWithRangesArgs<T>) {
  const fuzzy = useMemo(() => new Fuzzy(options), [options])

  return useCallback(
    (
      items: T[] | null | undefined,
      searchTerm: string | null | undefined
    ): FuzzySearchResult<T>[] => {
      if (!searchTerm || !items) {
        return (items ?? []).map(item => ({ item, ranges: [] }))
      }

      const haystack = items.map(item => getSearchableString(item))
      const [matches, info, order] = fuzzy.search(haystack, searchTerm)

      if (!order || !matches || !info) {
        return []
      }

      const result: FuzzySearchResult<T>[] = []

      for (let i = 0; i < order.length; i += 1) {
        const orderedIdx = order[i]
        const matchIdx = matches[orderedIdx]

        if (exists(orderedIdx) && exists(matchIdx)) {
          result.push({
            item: items[matchIdx],
            ranges: info.ranges[orderedIdx] ?? []
          })
        }
      }

      return result
    },
    [fuzzy, getSearchableString]
  )
}
